import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { motion } from 'framer-motion';

const EligibilityCheckSection = ({ currentLanguage, onEligibilityComplete }) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [isEligible, setIsEligible] = useState(false);

  const content = {
    EN: {
      title: "Eligibility Criteria",
      subtitle: "Please confirm you meet the following requirements:",
      criteria: [
        {
          id: 'age',
          text: 'I am 18 years of age or older',
          required: true
        },
        {
          id: 'resident',
          text: 'I am a resident of Canada',
          required: true
        },
        {
          id: 'id',
          text: 'I have valid government-issued identification',
          required: true
        },
        {
          id: 'address',
          text: 'I have a verifiable Canadian address',
          required: true
        },
        {
          id: 'phone',
          text: 'I have a valid phone number',
          required: true
        },
        {
          id: 'funds',
          text: 'I have the ability to load funds onto the card',
          required: true
        }
      ],
      checkEligibility: "Check My Eligibility",
      eligible: {
        title: "Congratulations! You\'re Eligible",
        message: "You meet all the requirements for a PSPXpress card. Ready to continue with your application?",
        continue: "Continue Application"
      },
      notEligible: {
        title: "Unfortunately, You Don't Meet All Requirements",
        message: "To be eligible for a PSPXpress card, you must meet all the listed criteria. Please review the requirements and try again when you meet all conditions.",
        tryAgain: "Try Again"
      }
    },
    FR: {
      title: "Critères d\'admissibilité",
      subtitle: "Veuillez confirmer que vous répondez aux exigences suivantes:",
      criteria: [
        {
          id: 'age',
          text: 'J\'ai 18 ans ou plus',
          required: true
        },
        {
          id: 'resident',
          text: 'Je suis résident du Canada',
          required: true
        },
        {
          id: 'id',
          text: 'J\'ai une pièce d\'identité valide émise par le gouvernement',
          required: true
        },
        {
          id: 'address',
          text: 'J\'ai une adresse canadienne vérifiable',
          required: true
        },
        {
          id: 'phone',
          text: 'J\'ai un numéro de téléphone valide',
          required: true
        },
        {
          id: 'funds',
          text: 'J\'ai la capacité de charger des fonds sur la carte',
          required: true
        }
      ],
      checkEligibility: "Vérifier mon admissibilité",
      eligible: {
        title: "Félicitations! Vous êtes admissible",
        message: "Vous répondez à toutes les exigences pour une carte PSPXpress. Prêt à continuer avec votre demande?",
        continue: "Continuer la demande"
      },
      notEligible: {
        title: "Malheureusement, vous ne répondez pas à toutes les exigences",
        message: "Pour être admissible à une carte PSPXpress, vous devez répondre à tous les critères listés. Veuillez revoir les exigences et réessayer lorsque vous répondez à toutes les conditions.",
        tryAgain: "Essayer de nouveau"
      }
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  const handleCheckboxChange = (criteriaId, checked) => {
    setCheckedItems(prev => ({
      ...prev,
      [criteriaId]: checked
    }));
  };

  const handleCheckEligibility = () => {
    const allChecked = text?.criteria?.every(criteria => checkedItems?.[criteria?.id] === true);
    setIsEligible(allChecked);
    setShowResult(true);
  };

  const handleContinue = () => {
    onEligibilityComplete(true);
  };

  const handleTryAgain = () => {
    setCheckedItems({});
    setShowResult(false);
    setIsEligible(false);
  };

  const allRequirementsMet = text?.criteria?.every(criteria => checkedItems?.[criteria?.id] === true);

  if (showResult) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className={`text-center p-8 rounded-2xl shadow-card ${
                isEligible ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
              }`}
            >
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                isEligible ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <Icon 
                  name={isEligible ? "CheckCircle" : "XCircle"} 
                  size={40} 
                  className={isEligible ? 'text-green-600' : 'text-red-600'} 
                />
              </div>

              <h2 className={`text-2xl font-bold mb-4 ${
                isEligible ? 'text-green-800' : 'text-red-800'
              }`}>
                {isEligible ? text?.eligible?.title : text?.notEligible?.title}
              </h2>

              <p className={`mb-8 leading-relaxed ${
                isEligible ? 'text-green-700' : 'text-red-700'
              }`}>
                {isEligible ? text?.eligible?.message : text?.notEligible?.message}
              </p>

              <Button
                variant="default"
                size="lg"
                onClick={isEligible ? handleContinue : handleTryAgain}
                className={`px-8 py-4 text-lg ${
                  isEligible ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
                iconName={isEligible ? "ArrowRight" : "RotateCcw"}
                iconPosition="right"
              >
                {isEligible ? text?.eligible?.continue : text?.notEligible?.tryAgain}
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {text?.title}
            </h2>
            <p className="text-lg text-gray-600">
              {text?.subtitle}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-card p-8"
          >
            <div className="space-y-6">
              {text?.criteria?.map((criteria, index) => (
                <motion.div
                  key={criteria?.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Checkbox
                    id={criteria?.id}
                    checked={checkedItems?.[criteria?.id] || false}
                    onChange={(checked) => handleCheckboxChange(criteria?.id, checked)}
                    className="flex-shrink-0"
                  />
                  <label 
                    htmlFor={criteria?.id}
                    className="text-gray-700 cursor-pointer flex-1"
                  >
                    {criteria?.text}
                  </label>
                  {criteria?.required && (
                    <span className="text-red-500 text-sm font-medium flex-shrink-0">
                      *
                    </span>
                  )}
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="mt-8 text-center"
            >
              <Button
                variant="default"
                size="lg"
                onClick={handleCheckEligibility}
                disabled={!allRequirementsMet}
                className="px-8 py-4 text-lg"
                iconName="CheckCircle"
                iconPosition="right"
              >
                {text?.checkEligibility}
              </Button>
              
              {!allRequirementsMet && (
                <p className="text-sm text-gray-500 mt-4">
                  {currentLanguage === 'FR' ?'Veuillez cocher toutes les cases pour continuer' :'Please check all boxes to continue'
                  }
                </p>
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default EligibilityCheckSection;